import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { buildTwentyFourMonthModel, calculateRevenue, calculateUnitEconomics } from "./investorModel";

const InvestorModelContext = createContext(null);

export const InvestorModelProvider = ({ data, children }) => {
  const [scenario, setScenarioName] = useState("base");
  const [unit, setUnit] = useState(data.unit_scenarios.base);
  const [revenue, setRevenue] = useState(data.revenue_defaults);
  const [fundPercents, setFundPercents] = useState(Object.fromEntries(data.use_of_funds.map((item) => [item.key, item.percent])));
  const [cityCount, setCityCount] = useState(10);

  const setScenario = useCallback((name) => {
    setScenarioName(name);
    setUnit(data.unit_scenarios[name]);
  }, [data.unit_scenarios]);

  const updateFund = useCallback((key, value) => {
    setFundPercents((current) => {
      const target = Math.max(1, Math.min(70, Number(value)));
      const difference = target - current[key];
      const otherKeys = Object.keys(current).filter((item) => item !== key);
      const otherTotal = otherKeys.reduce((sum, item) => sum + current[item], 0);
      const next = { ...current, [key]: target };
      otherKeys.forEach((item) => { next[item] = Math.max(0, current[item] - difference * (current[item] / otherTotal)); });
      const total = Object.values(next).reduce((sum, item) => sum + item, 0);
      const scale = 100 / total;
      Object.keys(next).forEach((item) => { next[item] *= scale; });
      return next;
    });
  }, []);

  const funds = useMemo(() => data.use_of_funds.map((item) => ({ ...item, percent: fundPercents[item.key], amount_inr: data.fundraise.raise_inr * fundPercents[item.key] / 100 })), [data.fundraise.raise_inr, data.use_of_funds, fundPercents]);
  const unitOutputs = useMemo(() => calculateUnitEconomics(unit), [unit]);
  const revenueOutputs = useMemo(() => calculateRevenue(revenue), [revenue]);
  const monthlyModel = useMemo(() => buildTwentyFourMonthModel({ fundraise: data.fundraise, funds, unit, revenue, cityCount }), [cityCount, data.fundraise, funds, revenue, unit]);
  const value = useMemo(() => ({ scenario, setScenario, unit, setUnit, unitOutputs, revenue, setRevenue, revenueOutputs, funds, updateFund, cityCount, setCityCount, monthlyModel }), [cityCount, funds, monthlyModel, revenue, revenueOutputs, scenario, setScenario, unit, unitOutputs, updateFund]);
  return <InvestorModelContext.Provider value={value}>{children}</InvestorModelContext.Provider>;
};

export const useInvestorModel = () => {
  const context = useContext(InvestorModelContext);
  if (!context) throw new Error("useInvestorModel must be used within InvestorModelProvider");
  return context;
};